import { useSearchParams } from 'react-router-dom-v5-compat';
import { useCallback } from 'react';
import { DbAppSettings } from '@harvestiq/iiq-common';
import { localStorage } from '../utils/localStorage';
import { environmentConfig } from '../environment-config';
import { useAppSettings } from '../contexts/index';
import { isNil } from 'lodash';
import { ENVIRONMENTS } from '@harvestiq/constants';

export enum FeatureFlags {
  EXAMPLE_FF = 'exampleFF',
  ENABLE_CLAIMS_UI = 'enableClaimsUi',
  ENABLE_SMS_CAMPAIGNS = 'enableSMSCampaigns',
  ENABLE_AGGREGATE_REPORTS = 'enableAggregateReports',
  ENABLE_COMBINED_QUOTE_CAMPAIGNS = 'enableCombinedQuoteCampaignsToSingleEmail',
  ENABLE_CUSTOM_SMS = 'enableCustomSMS',
}

const FEATURE_FLAG_DEFAULTS_BY_ENV: Record<
  ENVIRONMENTS,
  Record<FeatureFlags, boolean>
> = {
  development: {
    [FeatureFlags.ENABLE_CLAIMS_UI]: true,
    [FeatureFlags.EXAMPLE_FF]: true,
    [FeatureFlags.ENABLE_SMS_CAMPAIGNS]: false, // Default off as it is defined on org level
    [FeatureFlags.ENABLE_AGGREGATE_REPORTS]: true,
    [FeatureFlags.ENABLE_COMBINED_QUOTE_CAMPAIGNS]: false, // Default off as it is defined on org level
    [FeatureFlags.ENABLE_CUSTOM_SMS]: false, // Default off as it is defined on org level
  },
  test: {
    [FeatureFlags.ENABLE_CLAIMS_UI]: true,
    [FeatureFlags.EXAMPLE_FF]: true,
    [FeatureFlags.ENABLE_SMS_CAMPAIGNS]: false,
    [FeatureFlags.ENABLE_AGGREGATE_REPORTS]: true,
    [FeatureFlags.ENABLE_COMBINED_QUOTE_CAMPAIGNS]: false,
    [FeatureFlags.ENABLE_CUSTOM_SMS]: false,
  },
  staging: {
    [FeatureFlags.ENABLE_CLAIMS_UI]: true,
    [FeatureFlags.EXAMPLE_FF]: true,
    [FeatureFlags.ENABLE_SMS_CAMPAIGNS]: false,
    [FeatureFlags.ENABLE_AGGREGATE_REPORTS]: false,
    [FeatureFlags.ENABLE_COMBINED_QUOTE_CAMPAIGNS]: false,
    [FeatureFlags.ENABLE_CUSTOM_SMS]: false,
  },
  production: {
    [FeatureFlags.ENABLE_CLAIMS_UI]: true,
    [FeatureFlags.EXAMPLE_FF]: false,
    [FeatureFlags.ENABLE_SMS_CAMPAIGNS]: false,
    [FeatureFlags.ENABLE_AGGREGATE_REPORTS]: false,
    [FeatureFlags.ENABLE_COMBINED_QUOTE_CAMPAIGNS]: false,
    [FeatureFlags.ENABLE_CUSTOM_SMS]: false,
  },
};
/**
 * Returns a boolean featureEnabled value if the passed feature flag is enabled for the user.
 * e.g. const { featureEnabled: showExampleFeature } = useFeatureFlags(FeatureFlags.EXAMPLE_FF)
 * Feature flags are enabled for the user by passing a query param with the flag name and 'true':
 * e.g. ?exampleFF=true
 * Feature flag query params are evaluated once on load where the feature flag is stored in local storage.
 * Feature flags will be enabled for the user until local storage is cleared or flag is disabled.
 * Feature flags can be disabled by passing a query param with the flag name and anything other than 'true':
 * e.g. ?exampleFF or ?exampleFF=false
 * App feature flags are defined in appSettings and will override local storage and default feature flags.
 * @param featureFlagName
 * @returns { featureEnabled: boolean, setFeatureFlagsFromSearchParams: () => void}
 */
export const useFeatureFlags = (featureFlagName?: FeatureFlags) => {
  const { env } = environmentConfig;
  const [searchParams] = useSearchParams();

  const setFeatureFlagsFromSearchParams = useCallback(() => {
    searchParams.forEach((value, key) => {
      if (Object.values(FeatureFlags).includes(key as FeatureFlags)) {
        if (value.toLowerCase() === 'true') {
          localStorage.set(key, true);
        } else {
          localStorage.remove(key);
        }
      }
    });
  }, [searchParams]);

  if (!featureFlagName) {
    return { featureEnabled: false, setFeatureFlagsFromSearchParams };
  }

  const localStorageFeatureFlag = localStorage.get(featureFlagName) as
    | boolean
    | undefined;

  const defaultFeatureFlag = FEATURE_FLAG_DEFAULTS_BY_ENV[env][featureFlagName];

  const { appSettings } = useAppSettings();
  const appFeatureFlag = appSettings?.[
    featureFlagName as unknown as keyof DbAppSettings
  ] as boolean | null | undefined;

  const featureEnabled =
    // If there is a app feature flag is defined, use it above all else
    !isNil(appFeatureFlag)
      ? appFeatureFlag
      : // Fallback to local storage explicitly set by user
        (localStorageFeatureFlag ??
        // Fallback to default feature flags by environment
        defaultFeatureFlag);

  return { featureEnabled, setFeatureFlagsFromSearchParams };
};
